import React, { useState, useEffect, useRef } from 'react';
import { extractEmailAddress } from '../../utils/email.js';
import { useTranslation } from 'react-i18next';
import {
  useEmailAccounts, useSendEmail, useSaveDraft, useUpdateDraft, useDiscardDraft,
  useUploadAttachment, useDeleteAttachment, useDraftAttachments,
  type AttachmentMeta, type ComposeDraft, type ComposeType, type DraftRow,
} from '../../hooks/useEmail.js';
import { useAccountSignature } from '../../hooks/useSettings.js';
import { Icon } from '../Icon.js';
import { Button } from '../ui/Button.js';
import { Input } from '../ui/Input.js';
import { Select } from '../ui/Select.js';
import { IconButton } from '../ui/IconButton.js';
import { Drawer } from '../ui/Drawer.js';
import { useConfirm } from '../ui/ConfirmDialog.js';
import { SignaturePreview } from '../ui/SignaturePreview.js';
import { buildSigHtml } from '../../lib/signature.js';
import { ContactAutocomplete } from './ContactAutocomplete.js';
import { RichTextEditor, type RichTextEditorHandle } from '../ui/RichTextEditor.js';
import { htmlATexto } from '../../lib/textoEnriquecido.js';

interface Props {
  draft: ComposeDraft;
  type: ComposeType;
  onClose: () => void;
  onSent: () => void;
  savedDraftId?: number;
  inline?: boolean;
  onRecipientChange?: (email: string) => void;
}

// La versión en texto plano del correo: un párrafo es una línea en blanco,
// como en el resto de Evo Hub (lib/textoEnriquecido.ts). Antes los párrafos
// quedaban pegados con un solo salto.
function stripHtmlToText(html: string): string {
  return htmlATexto(html);
}

function isEmptyHtml(html: string): boolean {
  return !html || html === '<p></p>' || stripHtmlToText(html).trim() === '';
}

function formatBytes(b: number): string {
  if (b < 1024) return `${b}B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)}KB`;
  return `${(b / (1024 * 1024)).toFixed(1)}MB`;
}

function parseAddrs(s: string): string[] {
  return s.split(',').map(x => x.trim()).filter(Boolean);
}

export function draftRowToComposeDraft(row: DraftRow): ComposeDraft {
  return {
    accountName: row.accountName,
    to: parseAddrs(row.toAddresses ?? ''),
    cc: parseAddrs(row.ccAddresses ?? ''),
    bcc: parseAddrs(row.bccAddresses ?? ''),
    subject: row.subject ?? '',
    bodyHtml: row.bodyHtml ?? row.bodyText ?? '',
    inReplyTo: row.inReplyTo,
    references: row.referencesHeader ? row.referencesHeader.split(' ').filter(Boolean) : [],
    replyToMessageId: row.replyToMessageId,
  };
}

export function EmailComposer({ draft, type, onClose, onSent, savedDraftId, inline = false, onRecipientChange }: Props) {
  const { t } = useTranslation();
  const { data: accounts = [] } = useEmailAccounts();
  const sendEmail = useSendEmail();
  const saveDraft = useSaveDraft();
  const updateDraftMut = useUpdateDraft();
  const discardDraft = useDiscardDraft();
  const { confirm: confirmAction, dialog: confirmDialog } = useConfirm();
  const uploadAtt = useUploadAttachment();
  const deleteAtt = useDeleteAttachment();

  const [accountName, setAccountName] = useState(draft.accountName || accounts[0] || '');
  const [to, setTo] = useState(draft.to.join(', '));
  const [cc, setCc] = useState(draft.cc.join(', '));
  const [bcc, setBcc] = useState(draft.bcc?.join(', ') ?? '');
  const [subject, setSubject] = useState(draft.subject);
  const [body, setBody] = useState(draft.bodyHtml || '');
  const [quotedHtml] = useState(draft.quotedHtml ?? '');
  const [showCc, setShowCc] = useState(draft.cc.length > 0);
  const [showBcc, setShowBcc] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingEmptySubject, setPendingEmptySubject] = useState(false);
  const [draftId, setDraftId] = useState<number | undefined>(savedDraftId);
  const [draftSaved, setDraftSaved] = useState(false);
  const [attachments, setAttachments] = useState<AttachmentMeta[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);

  const draftIdRef = useRef<number | undefined>(savedDraftId);
  const creatingDraftRef = useRef(false);
  const editorRef = useRef<RichTextEditorHandle>(null);
  const toRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const attInitialized = useRef(false);

  const { data: sigData } = useAccountSignature(accountName || undefined);
  const { data: existingAtts = [] } = useDraftAttachments(savedDraftId ?? null);

  const title = type === 'compose' ? t('email.compose.title')
    : type === 'reply' ? t('email.reply')
    : type === 'reply-all' ? t('email.replyAll')
    : t('email.forward');

  // Load attachments from an existing draft (only once)
  useEffect(() => {
    if (!attInitialized.current && existingAtts.length > 0) {
      attInitialized.current = true;
      setAttachments(existingAtts);
    }
  }, [existingAtts]);

  // Sync accountName when accounts load
  useEffect(() => {
    if (!accountName && accounts.length > 0) {
      setAccountName(draft.accountName || accounts[0] || '');
    }
  }, [accounts, draft.accountName, accountName]);

  // Keep draftIdRef in sync
  useEffect(() => { draftIdRef.current = draftId; }, [draftId]);

  // Focus on mount
  useEffect(() => {
    requestAnimationFrame(() => {
      if (type === 'compose' && toRef.current) {
        toRef.current.focus();
      } else {
        editorRef.current?.focus();
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Notify parent of recipient changes (for ClaudePanel compose context)
  useEffect(() => {
    onRecipientChange?.(extractEmailAddress(to));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [to]);

  // Auto-save: 5s debounce after last keystroke
  useEffect(() => {
    if (!to.trim() && !subject.trim() && isEmptyHtml(body)) return;
    const timer = setTimeout(() => {
      const payload = buildDraftPayload();
      const id = draftIdRef.current;
      if (id) {
        updateDraftMut.mutate({ id, ...payload });
      } else if (!creatingDraftRef.current) {
        // Bug real (2026-08-12): sin este guard, escribir seguido a través
        // del límite de 5s podía disparar DOS creaciones de borrador
        // concurrentes (ninguna veía todavía el id de la otra) — la que
        // resuelve última pisa draftId/draftIdRef, y la PRIMERA queda
        // huérfana en el servidor, invisible para el composer — un discard
        // posterior nunca la iba a poder borrar porque el cliente no sabía
        // que existía.
        creatingDraftRef.current = true;
        saveDraft.mutate(payload, {
          onSuccess: saved => {
            setDraftId(saved.id);
            draftIdRef.current = saved.id;
            creatingDraftRef.current = false;
          },
          onError: () => { creatingDraftRef.current = false; },
        });
      }
    }, 5000);
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [to, cc, bcc, subject, body, accountName]);

  function buildDraftPayload() {
    return {
      accountName,
      toAddresses: to || null,
      ccAddresses: cc || null,
      bccAddresses: bcc || null,
      subject: subject || null,
      bodyHtml: body || null,
      bodyText: stripHtmlToText(body) || null,
      inReplyTo: draft.inReplyTo ?? null,
      referencesHeader: draft.references.join(' ') || null,
      replyToMessageId: draft.replyToMessageId ?? null,
      attachmentIds: attachments.map(a => a.id),
    };
  }

  function handleSaveDraft() {
    if (!accountName) { setError(t('email.compose.no_account_error')); return; }
    setError(null);
    const payload = buildDraftPayload();
    const onErr = (err: unknown) =>
      setError(err instanceof Error ? err.message : t('email.compose.draft_save_error'));
    if (draftId) {
      updateDraftMut.mutate({ id: draftId, ...payload }, {
        onSuccess: () => { setDraftSaved(true); setTimeout(() => setDraftSaved(false), 2000); },
        onError: onErr,
      });
    } else {
      saveDraft.mutate(payload, {
        onSuccess: saved => {
          setDraftId(saved.id);
          draftIdRef.current = saved.id;
          setDraftSaved(true);
          setTimeout(() => setDraftSaved(false), 2000);
        },
        onError: onErr,
      });
    }
  }

  function handleSend() {
    setError(null);
    const toList = parseAddrs(to);
    if (toList.length === 0) { setError(t('email.compose.no_recipient_error')); return; }

    if (type === 'compose' && !subject.trim() && !pendingEmptySubject) {
      setError(t('email.compose.empty_subject_warning'));
      setPendingEmptySubject(true);
      return;
    }
    setPendingEmptySubject(false);

    const sig = sigData?.signature ?? null;
    const sigHtml = sig ? buildSigHtml(sig) : '';
    const fullHtml = (body || '<p></p>') + sigHtml + (quotedHtml ? `<br><br>${quotedHtml}` : '');
    const fullText = stripHtmlToText(fullHtml);

    sendEmail.mutate(
      {
        accountName,
        to: toList,
        cc: parseAddrs(cc),
        bcc: parseAddrs(bcc),
        subject,
        html: fullHtml,
        text: fullText,
        inReplyTo: draft.inReplyTo,
        references: draft.references,
        replyToMessageId: draft.replyToMessageId,
        attachmentIds: attachments.map(a => a.id),
      },
      {
        onSuccess: () => {
          if (draftIdRef.current) discardDraft.mutate(draftIdRef.current);
          onSent();
        },
        onError: (err) => setError(err instanceof Error ? err.message : String(err)),
      }
    );
  }

  async function handleDiscard() {
    if (draftId) {
      const ok = await confirmAction({ title: t('email.compose.discard_draft_confirm'), tone: 'danger' });
      if (!ok) return;
      // Bug real (2026-08-12): esto era `discardDraft.mutate(draftId); onClose();`
      // — cerraba el composer SIN ESPERAR la respuesta del borrado. Si el
      // DELETE fallaba (red, 404 por un borrador ya borrado en otra pestaña),
      // nadie se enteraba — el composer se cerraba igual, el borrador podía
      // seguir vivo en el servidor, y desde afuera se veía exactamente como
      // "discard no funciona". Ahora espera la respuesta: solo cierra si el
      // borrado realmente se confirmó; si falla, muestra el error y deja el
      // composer abierto para reintentar.
      try {
        await discardDraft.mutateAsync(draftId);
        onClose();
      } catch (err) {
        setError(err instanceof Error ? err.message : t('email.compose.discard_error'));
      }
      return;
    }
    if (to.trim() || subject.trim() || !isEmptyHtml(body)) {
      const ok = await confirmAction({ title: t('email.compose.discard_confirm'), tone: 'warning' });
      if (!ok) return;
    }
    onClose();
  }

  // Compartido entre el input de archivo (click) y el drop de arrastrar —
  // mismo bucle, cada archivo se sube independiente (ya soportaba varios a
  // la vez; lo que faltaba era la forma más natural de seleccionarlos).
  function processFiles(files: File[]) {
    if (!files.length) return;
    for (const file of files) {
      if (file.size > 25 * 1024 * 1024) { setError(t('email.compose.attachment_too_large')); continue; }
      const formData = new FormData();
      formData.append('file', file);
      if (draftIdRef.current) formData.append('draftId', String(draftIdRef.current));
      uploadAtt.mutate(formData, {
        onSuccess: att => setAttachments(prev => [...prev, att]),
        onError: (err: Error) => setError(err.message || t('email.compose.attachment_failed')),
      });
    }
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    processFiles(Array.from(e.target.files ?? []));
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  function handleRemoveAttachment(id: number) {
    deleteAtt.mutate(id, {
      onSuccess: () => setAttachments(prev => prev.filter(a => a.id !== id)),
    });
  }

  const isSavingDraft = saveDraft.isPending || updateDraftMut.isPending;
  const sig = sigData?.signature ?? null;

  const panelStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    width: '100%',
    background: 'var(--evo-bg)',
    outline: isDragOver ? '2px solid var(--evo-cyan)' : 'none',
    outlineOffset: '-2px',
    transition: 'outline-color 120ms linear',
  };

  // El diálogo de confirmación va DENTRO de inner, no en el return de abajo.
  // Bug real (2026-08-31): estaba solo en la rama del Drawer, y el composer se
  // monta SIEMPRE con inline (EmailPage lo pone en el panel derecho) — así que
  // en la app real nunca se montaba. handleDiscard hace
  // `await confirmAction(...)`, que devuelve una promesa que resuelve cuando
  // alguien aprieta un botón del diálogo; sin diálogo montado no había quién
  // la resolviera, el await quedaba colgado para siempre y el DELETE nunca
  // salía. Desde afuera: apretás Descartar y no pasa absolutamente nada, sin
  // error ni en pantalla ni en consola.
  const inner = (
    <>
    {confirmDialog}
    <div
      style={panelStyle}
      onDragEnter={e => { e.preventDefault(); setIsDragOver(true); }}
      onDragOver={e => { e.preventDefault(); setIsDragOver(true); }}
      onDragLeave={e => { if (e.currentTarget === e.target) setIsDragOver(false); }}
      onDrop={e => {
        e.preventDefault();
        setIsDragOver(false);
        processFiles(Array.from(e.dataTransfer.files));
      }}
    >
        {/* Header — fixed height, DS pattern */}
        <div
          className="shrink-0"
          style={{
            height: 'var(--evo-toolbar-height)',
            padding: '0 var(--evo-space-5)',
            borderBottom: '1px solid var(--evo-stroke)',
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--evo-space-2)',
          }}
        >
          <span style={{ flex: 1, minWidth: 0, fontSize: 'var(--evo-fs-body)', fontWeight: 'var(--evo-fw-medium)', color: 'var(--evo-fg-1)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {title}
          </span>
          <div style={{ display: 'flex', gap: 'var(--evo-space-2)', flexShrink: 0, alignItems: 'center' }}>
            <Button
              variant="solid"
              tone="cyan"
              size="sm"
              icon={<Icon name="send" size={13} />}
              loading={sendEmail.isPending}
              onClick={handleSend}
            >
              {sendEmail.isPending ? t('email.compose.sending') : t('email.compose.sendBtn')}
            </Button>
            <Button
              variant="outline"
              tone={draftSaved ? 'cyan' : 'muted'}
              size="sm"
              loading={isSavingDraft}
              onClick={handleSaveDraft}
            >
              {isSavingDraft ? '…' : draftSaved ? t('email.compose.draftSaved') : t('email.compose.saveDraft')}
            </Button>
            <Button
              variant="outline"
              tone="muted"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
            >
              {t('email.compose.attach')}
            </Button>
            <Button
              variant="outline"
              tone="danger"
              size="sm"
              onClick={() => { void handleDiscard(); }}
            >
              {t('email.compose.discardBtn')}
            </Button>
            <IconButton icon={<Icon name="close" size={16} />} ariaLabel={t('common.close')} onClick={onClose} variant="ghost" tone="muted" size="sm" />
          </div>
        </div>
        {(error || sendEmail.isSuccess) && (
          <div className="shrink-0 flex items-center gap-2 px-5 py-1" style={{ borderBottom: '1px solid var(--evo-stroke)' }}>
            {error && <span className="text-xs" style={{ color: '#ef4444' }}>{error}</span>}
            {sendEmail.isSuccess && <span className="text-xs" style={{ color: 'var(--evo-cyan)' }}>{t('email.compose.sent')}</span>}
          </div>
        )}

        {/* Fields */}
        <div className="flex flex-col gap-0 shrink-0" style={{ borderBottom: '1px solid var(--evo-stroke)' }}>
          {/* From */}
          <FieldRow label={t('email.compose.fieldFrom')}>
            <Select
              variant="naked"
              value={accountName || null}
              onChange={v => setAccountName(v ?? '')}
              options={accounts.map(a => ({ value: a, label: a }))}
              size="sm"
            />
          </FieldRow>

          {/* To */}
          <FieldRow label={t('email.compose.fieldTo')}>
            <ContactAutocomplete
              inputRef={toRef}
              value={to}
              onChange={setTo}
              placeholder="recipient@example.com"
            />
            <div className="flex gap-1 shrink-0">
              {!showCc && <FieldToggle label={t('email.compose.fieldCc')} onClick={() => setShowCc(true)} />}
              {!showBcc && <FieldToggle label={t('email.compose.fieldBcc')} onClick={() => setShowBcc(true)} />}
            </div>
          </FieldRow>

          {showCc && (
            <FieldRow label={t('email.compose.fieldCc')}>
              <ContactAutocomplete value={cc} onChange={setCc} placeholder="cc@example.com" />
              <IconButton variant="ghost" tone="muted" size="sm" ariaLabel={t('email.compose.fieldCc')} icon={<Icon name="close" size={12} />} onClick={() => { setShowCc(false); setCc(''); }} />
            </FieldRow>
          )}

          {showBcc && (
            <FieldRow label={t('email.compose.fieldBcc')}>
              <ContactAutocomplete value={bcc} onChange={setBcc} placeholder="bcc@example.com" />
              <IconButton variant="ghost" tone="muted" size="sm" ariaLabel={t('email.compose.fieldBcc')} icon={<Icon name="close" size={12} />} onClick={() => { setShowBcc(false); setBcc(''); }} />
            </FieldRow>
          )}

          {/* Subject */}
          <FieldRow label={t('email.compose.fieldSubject')}>
            <Input variant="naked" value={subject} onChange={setSubject} size="sm" />
          </FieldRow>
        </div>

        {/* Rich text editor */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <RichTextEditor
            ref={editorRef}
            value={body}
            onChange={setBody}
            placeholder={t('email.compose.bodyPlaceholder', { defaultValue: 'Write your message…' })}
            enableHeadings
            enableCodeBlock
          />

          {/* Vista previa de la firma. Antes se pintaba a mano, línea por
              línea, como texto plano: con una firma HTML mostraba las
              etiquetas en crudo — justo lo contrario de una previa. Ahora usa
              el primitive, que distingue HTML de texto con el mismo criterio
              que buildSigHtml y renderiza sobre blanco, el fondo real del
              correo. */}
          {sig && (
            <div
              className="px-4 py-2 shrink-0"
              style={{ borderTop: '1px solid var(--evo-stroke)', userSelect: 'none' }}
            >
              <SignaturePreview value={sig} hideLabel />
            </div>
          )}

          {/* Quoted original (reply/forward) — rendered outside Tiptap to preserve full HTML */}
          {quotedHtml && (
            <div
              className="px-4 py-3 shrink-0"
              style={{
                borderTop: '1px solid var(--evo-stroke)',
                overflowY: 'auto',
                maxHeight: '280px',
                background: '#fff',
                fontSize: '13px',
                lineHeight: 1.5,
              }}
              dangerouslySetInnerHTML={{ __html: quotedHtml }}
            />
          )}

          {/* Attachment list */}
          {attachments.length > 0 && (
            <div
              className="flex flex-wrap gap-1.5 px-4 py-2 shrink-0"
              style={{ borderTop: '1px solid var(--evo-stroke)' }}
            >
              {attachments.map(att => (
                <div
                  key={att.id}
                  className="flex items-center gap-1 text-xs px-2 py-1 rounded"
                  style={{ background: 'var(--evo-bg-2)', border: '1px solid var(--evo-stroke)' }}
                >
                  <span style={{ color: 'var(--evo-fg-2)' }}>{att.filename}</span>
                  <span style={{ color: 'var(--evo-fg-3)' }}>({formatBytes(att.sizeBytes)})</span>
                  <IconButton
                    variant="ghost"
                    tone="muted"
                    size="sm"
                    ariaLabel={t('common.remove')}
                    icon={<span style={{ fontSize: '11px', lineHeight: 1 }}>✕</span>}
                    onClick={() => handleRemoveAttachment(att.id)}
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        <input
          ref={fileInputRef}
          type="file"
          multiple
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />
      </div>
    </>
  );

  if (inline) return inner;

  return (
    <>
      <Drawer
        open
        onClose={onClose}
        side="right"
        size="md"
        bodyPadding={0}
      >
        {inner}
      </Drawer>
    </>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────────

function FieldRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div
      className="flex items-center gap-2 px-4"
      style={{ borderBottom: '1px solid var(--evo-stroke)', minHeight: '40px' }}
    >
      <span className="text-xs shrink-0" style={{ color: 'var(--evo-fg-3)', width: '52px' }}>{label}</span>
      {children}
    </div>
  );
}

function FieldToggle({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <Button variant="outline" tone="muted" size="xs" onClick={onClick}>
      {label}
    </Button>
  );
}

